/**
 * Dashboard context provider for Creative Analytics Dashboard (Phase 28).
 *
 * Manages all filter state via URL search params so that dashboard state
 * is bookmarkable and shareable. Provides template/project lists from
 * existing hooks, and exposes edge state from the first API response.
 *
 * FR-09: Template switch resets all filters to defaults.
 */

import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext';
import { useProjects } from '@/hooks/useProjects';
import { useTemplates } from '@/hooks/useTemplates';
import type { Project } from '@/types';
import type {
  DashboardFilters,
  DashboardTab,
  InsightsSubView,
  DashboardEdgeState,
} from '@/types/dashboard';
import type { Template } from '@/types/template';

// ============================================================================
// CONTEXT TYPE
// ============================================================================

export interface DashboardContextValue {
  /** Current computed filters from URL params */
  filters: DashboardFilters;

  /** Whether we are in aggregate mode (projectId === 'all') */
  filterMode: 'single-project' | 'aggregate';

  /** Set a single filter value (preserves other params) */
  setFilter: <K extends keyof DashboardFilters>(key: K, value: DashboardFilters[K]) => void;

  /** Switch active tab (clears drilldown params) */
  setTab: (tab: DashboardTab) => void;

  /** Switch template — FR-09: resets project, drilldown, view, invalidates cache */
  switchTemplate: (templateId: string) => void;

  /** Select a project -- auto-resolves template, clears drilldown */
  selectProject: (projectId: string) => void;

  /** Enter aggregate mode -- sets project to 'all', enables template picker */
  enterAggregateMode: () => void;

  /** Set custom date range */
  setCustomDateRange: (from: string, to: string) => void;

  /** Clear custom date range (revert to preset) */
  clearCustomDateRange: (preset: '7d' | '30d' | '90d') => void;

  /** Clear all drilldown params */
  clearDrilldown: () => void;

  /** Edge state from the latest API response (set externally) */
  edgeState: DashboardEdgeState | null;

  /** Update edge state when API response arrives */
  setEdgeState: (state: DashboardEdgeState) => void;

  /** Available templates for the team */
  templates: Template[];

  /** Currently active template object (resolved from filters.templateId) */
  activeTemplate: Template | null;

  /** Whether templates are loading */
  templatesLoading: boolean;

  /** Available projects for the team */
  projects: { id: string; name: string; templateId?: string }[];

  /** Whether projects are loading */
  projectsLoading: boolean;
}

// ============================================================================
// DEFAULTS
// ============================================================================

const DEFAULT_PERIOD = '30d';
const DEFAULT_MEDIA_TYPE = 'all';
const DEFAULT_TAB: DashboardTab = 'verdicts';
const DEFAULT_INSIGHTS_SUB_VIEW: InsightsSubView = 'drivers';

// ============================================================================
// CONTEXT
// ============================================================================

const DashboardContext = createContext<DashboardContextValue | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export interface DashboardProviderProps {
  children: ReactNode;
}

export function DashboardProvider({ children }: DashboardProviderProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;

  // Fetch templates and projects from existing hooks
  const { data: templateData, isLoading: templatesLoading } = useTemplates(teamId);
  const { data: projectsData, isLoading: projectsLoading } = useProjects(teamId);

  // Edge state (set from first API response meta)
  const [edgeState, setEdgeState] = useState<DashboardEdgeState | null>(null);

  // Derive templates list
  const templates = useMemo(
    () => templateData?.templates ?? [],
    [templateData]
  );

  // Derive projects list (sorted alphabetically, includes templateId)
  const projects = useMemo<{ id: string; name: string; templateId?: string }[]>(
    () =>
      (projectsData ?? [])
        .map((p: Project) => ({
          id: p.projectId,
          name: p.name,
          templateId: p.templateId,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [projectsData]
  );

  // Resolve default template: first template with isDefault, else first template
  const defaultTemplateId = useMemo(() => {
    const def = templates.find((t) => t.isDefault);
    return def?.templateId ?? templates[0]?.templateId ?? '';
  }, [templates]);

  // Default to first project alphabetically that has a template (FILT-05)
  const defaultProjectId = useMemo(() => {
    const firstWithTemplate = projects.find(p => p.templateId);
    return firstWithTemplate?.id ?? 'all';
  }, [projects]);

  // Compute filters from URL params (FILT-05/06/07 project-first model)
  const filters = useMemo<DashboardFilters>(() => {
    const urlProject = searchParams.get('project');
    const urlTemplate = searchParams.get('template');
    const period = (searchParams.get('period') as DashboardFilters['period']) || DEFAULT_PERIOD;
    const fromDate = searchParams.get('from') || undefined;
    const toDate = searchParams.get('to') || undefined;
    const mediaType = (searchParams.get('media') as DashboardFilters['mediaType']) || DEFAULT_MEDIA_TYPE;
    const activeTab = (searchParams.get('tab') as DashboardTab) || DEFAULT_TAB;
    const insightsSubView = (searchParams.get('view') as InsightsSubView) || DEFAULT_INSIGHTS_SUB_VIEW;
    const drilldownGate = searchParams.get('gate') || undefined;
    const drilldownVerdict = searchParams.get('verdict') || undefined;
    const drilldownAttribute = searchParams.get('attr') || undefined;

    // FILT-07: URL backward compat -- template in URL but no project => aggregate mode
    let projectId: string | 'all';
    let templateId: string;

    if (urlProject) {
      // Explicit project in URL
      projectId = urlProject;
      if (projectId === 'all') {
        // Aggregate mode -- use template from URL or default
        templateId = urlTemplate || defaultTemplateId;
      } else {
        // FILT-06: Single-project mode -- auto-resolve template from project
        const proj = projects.find(p => p.id === projectId);
        templateId = proj?.templateId || urlTemplate || defaultTemplateId;
      }
    } else if (urlTemplate && !urlProject) {
      // FILT-07: Legacy URL with template but no project => aggregate mode
      projectId = 'all';
      templateId = urlTemplate;
    } else {
      // No URL params -- FILT-05: default to first project
      projectId = defaultProjectId;
      const proj = projects.find(p => p.id === defaultProjectId);
      templateId = proj?.templateId || defaultTemplateId;
    }

    return {
      templateId,
      projectId,
      period,
      fromDate,
      toDate,
      mediaType,
      activeTab,
      insightsSubView,
      drilldownGate,
      drilldownVerdict,
      drilldownAttribute,
    };
  }, [searchParams, defaultTemplateId, defaultProjectId, projects]);

  // Filter mode: single-project or aggregate
  const filterMode = useMemo<'single-project' | 'aggregate'>(
    () => filters.projectId === 'all' ? 'aggregate' : 'single-project',
    [filters.projectId]
  );

  // Active template object
  const activeTemplate = useMemo(
    () => templates.find((t) => t.templateId === filters.templateId) ?? null,
    [templates, filters.templateId]
  );

  // Set a single filter value
  const setFilter = useCallback(
    <K extends keyof DashboardFilters>(key: K, value: DashboardFilters[K]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const paramMap: Record<string, string> = {
          templateId: 'template',
          projectId: 'project',
          period: 'period',
          fromDate: 'from',
          toDate: 'to',
          mediaType: 'media',
          activeTab: 'tab',
          insightsSubView: 'view',
          drilldownGate: 'gate',
          drilldownVerdict: 'verdict',
          drilldownAttribute: 'attr',
        };
        const paramKey = paramMap[key] ?? key;

        if (value === undefined || value === null || value === '' || value === 'all') {
          // Remove param for default/empty values (except project 'all' — just remove)
          next.delete(paramKey);
        } else {
          next.set(paramKey, String(value));
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // Switch tab: set tab, clear drilldown params
  const setTab = useCallback(
    (tab: DashboardTab) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        // Clear drilldown params on tab switch
        next.delete('gate');
        next.delete('verdict');
        next.delete('attr');
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // FR-09: Switch template — reset project, drilldown, view, invalidate cache
  const switchTemplate = useCallback(
    (templateId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('template', templateId);
        // Reset to defaults
        next.delete('project');
        next.delete('gate');
        next.delete('verdict');
        next.delete('attr');
        next.delete('view');
        return next;
      }, { replace: true });

      // Invalidate all dashboard queries so data refetches for new template
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    [setSearchParams, queryClient]
  );

  // FILT-08: Select a project -- auto-resolves template, clears drilldown
  const selectProject = useCallback(
    (projectId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (projectId === 'all') {
          next.delete('project');
        } else {
          next.set('project', projectId);
        }
        // Auto-resolve template from project
        const proj = projects.find(p => p.id === projectId);
        if (proj?.templateId && projectId !== 'all') {
          next.set('template', proj.templateId);
        }
        // FILT-08: Clear drilldown params on project change
        next.delete('gate');
        next.delete('verdict');
        next.delete('attr');
        return next;
      }, { replace: true });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    [setSearchParams, projects, queryClient]
  );

  // Enter aggregate mode -- sets project to 'all', enables template picker
  const enterAggregateMode = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('project', 'all');
      // Keep current template
      // Clear drilldowns
      next.delete('gate');
      next.delete('verdict');
      next.delete('attr');
      return next;
    }, { replace: true });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }, [setSearchParams, queryClient]);

  // Set custom date range
  const setCustomDateRange = useCallback(
    (from: string, to: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('period', 'custom');
        next.set('from', from);
        next.set('to', to);
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // Clear custom date range (revert to preset)
  const clearCustomDateRange = useCallback(
    (preset: '7d' | '30d' | '90d') => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('period', preset);
        next.delete('from');
        next.delete('to');
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // Clear all drilldown params
  const clearDrilldown = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('gate');
      next.delete('verdict');
      next.delete('attr');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const value = useMemo<DashboardContextValue>(
    () => ({
      filters,
      filterMode,
      setFilter,
      setTab,
      switchTemplate,
      selectProject,
      enterAggregateMode,
      setCustomDateRange,
      clearCustomDateRange,
      clearDrilldown,
      edgeState,
      setEdgeState,
      templates,
      activeTemplate,
      templatesLoading,
      projects,
      projectsLoading,
    }),
    [
      filters,
      filterMode,
      setFilter,
      setTab,
      switchTemplate,
      selectProject,
      enterAggregateMode,
      setCustomDateRange,
      clearCustomDateRange,
      clearDrilldown,
      edgeState,
      templates,
      activeTemplate,
      templatesLoading,
      projects,
      projectsLoading,
    ]
  );

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Access the dashboard context. Must be used within DashboardProvider.
 */
export function useDashboardContext(): DashboardContextValue {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboardContext must be used within a DashboardProvider');
  }
  return context;
}
